import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { posProductService } from '../services/pos.service'

export const POS_PRODUCT_CATALOG_QUERY_KEY = ['pos-products'] as const

interface UsePosProductCatalogOptions {
  search: string
  useCaseId: string
  sucursalId: number | null
}

export function usePosProductCatalog({ search, useCaseId, sucursalId }: UsePosProductCatalogOptions) {
  const queryClient = useQueryClient()
  const normalizedSearch = search.trim()
  const query = useInfiniteQuery({
    queryKey: [
      ...POS_PRODUCT_CATALOG_QUERY_KEY,
      sucursalId,
      normalizedSearch,
      useCaseId,
    ],
    queryFn: ({ pageParam, signal }) => posProductService.getProducts({
      search: normalizedSearch || undefined,
      use_case_id: useCaseId === 'all' ? undefined : Number(useCaseId),
      page: pageParam,
      per_page: 20,
    }, signal),
    initialPageParam: 1,
    enabled: sucursalId !== null,
    getNextPageParam: (lastPage) => {
      const currentPage = Number(lastPage.current_page ?? 1)
      const lastPageNumber = Number(lastPage.last_page ?? currentPage)

      return currentPage < lastPageNumber ? currentPage + 1 : undefined
    },
  })

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: POS_PRODUCT_CATALOG_QUERY_KEY }),
    [queryClient],
  )

  return {
    products: sucursalId === null
      ? []
      : query.data?.pages.flatMap((page) => page.data) ?? [],
    isLoading: query.isPending,
    isRefreshing: query.isFetching && !query.isFetchingNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    fetchNextPage: query.fetchNextPage,
    refresh,
  }
}
